import { format, formatDistanceToNow, isValid, parse } from 'date-fns';
import { vi } from 'date-fns/locale';

export function formatDate(date: string | Date | undefined, formatString: string = 'dd/MM/yyyy') {
  if (!date) return 'N/A';
  return format(date, formatString);
}

export function formatCurrency(amount: number, currency: string = 'VND'): string {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function getRelativeTime(date: string | Date | undefined) {
  if (!date) return '';

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - new Date(date).getTime()) / 1000);

  if (diffInSeconds < 60) return 'Vừa xong';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} phút trước`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} giờ trước`;

  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} ngày trước`;

  return formatDistanceToNow(date, { addSuffix: true, locale: vi });
}

/**
 * Format date for SEO-friendly URL (YYYY-MM-DD format)
 */
export function formatDateForUrl(date: Date | string): string {
  if (!date) return format(new Date(), 'yyyy-MM-dd');

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, 'yyyy-MM-dd');
}

/**
 * Parse date from URL parameter - supports both YYYY-MM-DD and DD/MM/YYYY formats
 * Returns the date in YYYY-MM-DD format for consistency
 */
export function parseDateFromUrl(dateString: string): string {
  if (!dateString) return formatDateForUrl(new Date());

  // If already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const date = new Date(dateString);
    return isValid(date) ? dateString : formatDateForUrl(new Date());
  }

  // Try to parse DD/MM/YYYY format (legacy support)
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
    const parsedDate = parse(dateString, 'dd/MM/yyyy', new Date());
    if (isValid(parsedDate)) {
      return format(parsedDate, 'yyyy-MM-dd');
    }
  }

  // Try to parse DD-MM-YYYY format (alternative hyphen format)
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
    const parsedDate = parse(dateString, 'dd-MM-yyyy', new Date());
    if (isValid(parsedDate)) {
      return format(parsedDate, 'yyyy-MM-dd');
    }
  }

  // If all parsing fails, return today's date
  return formatDateForUrl(new Date());
}

/**
 * Get today's date in SEO-friendly URL format
 */
export function getTodayUrlFormat(): string {
  return formatDateForUrl(new Date());
}

const DAY_ORDER: Record<string, number> = {
  Monday: 2,
  Tuesday: 3,
  Wednesday: 4,
  Thursday: 5,
  Friday: 6,
  Saturday: 7,
  Sunday: 8,
};

function dayLabel(index: number, capitalize = false): string {
  if (index === 8) return capitalize ? 'Chủ nhật' : 'chủ nhật';
  return `thứ ${index}`;
}

/**
 * Format a comma-separated English weekday string into Vietnamese.
 * e.g. "Monday,Tuesday,Wednesday,Thursday,Friday,Saturday,Sunday," -> "Thứ 2 đến chủ nhật"
 *      "Saturday,Sunday,"                                           -> "Cuối tuần"
 *      "Monday,Thursday,Friday,"                                    -> "Thứ 2, 5, 6"
 */
export function formatWeekDays(weekDays: string | undefined | null): string {
  if (!weekDays) return '';

  const indices = weekDays
    .split(',')
    .map((d) => d.trim())
    .filter((d) => d in DAY_ORDER)
    .map((d) => DAY_ORDER[d])
    .sort((a, b) => a - b);

  if (indices.length === 0) return '';

  // Special shortcut cases
  if (indices.length === 7) return 'Thứ 2 đến chủ nhật';
  if (indices.length === 6 && indices[0] === 2 && indices[5] === 7) return 'Thứ 2 đến thứ 7';
  if (indices.length === 5 && indices[0] === 2 && indices[4] === 6) return 'Thứ 2 đến thứ 6';
  if (indices.length === 2 && indices[0] === 7 && indices[1] === 8) return 'Cuối tuần';

  // Group consecutive runs
  const runs: number[][] = [];
  let run: number[] = [indices[0]];
  for (let i = 1; i < indices.length; i++) {
    if (indices[i] === indices[i - 1] + 1) {
      run.push(indices[i]);
    } else {
      runs.push(run);
      run = [indices[i]];
    }
  }
  runs.push(run);

  const parts: string[] = [];
  for (const r of runs) {
    if (r.length >= 3) {
      const first = r[0] === 8 ? 'chủ nhật' : `thứ ${r[0]}`;
      const last = dayLabel(r[r.length - 1]);
      parts.push(`${first.charAt(0).toUpperCase() + first.slice(1)} đến ${last}`);
    } else {
      for (const idx of r) {
        parts.push(idx === 8 ? 'chủ nhật' : String(idx));
      }
    }
  }

  // Merge numeric labels — prefix first non-range group with "Thứ"
  const merged: string[] = [];
  let numericBuffer: string[] = [];

  const flushBuffer = () => {
    if (numericBuffer.length > 0) {
      merged.push(`Thứ ${numericBuffer.join(', ')}`);
      numericBuffer = [];
    }
  };

  for (const part of parts) {
    if (/^\d+$/.test(part)) {
      numericBuffer.push(part);
    } else if (part === 'chủ nhật') {
      flushBuffer();
      merged.push('chủ nhật');
    } else {
      flushBuffer();
      merged.push(part);
    }
  }
  flushBuffer();

  return merged.join(', ');
}
